// Helpers de apresentação do dashboard RAIZ. Puros → testáveis.
import type {
  Presence, CalibrationStatus, ExcursionStatus, RaizBatteryProjection, RaizDeviceHealth, RaizDeviceHibernation,
  RaizDeviceOverview,
} from './types'
import { cmpFwVersions } from './firmwareFormat'

export interface PresenceMeta {
  label: string
  color: 'success' | 'warning' | 'error' | 'default'
}

const PRESENCE_META: Record<Presence, PresenceMeta> = {
  live: { label: 'Ao vivo', color: 'success' },
  stale: { label: 'Atrasado', color: 'warning' },
  offline: { label: 'Offline', color: 'error' },
  never: { label: 'Sem leitura', color: 'default' },
}

export const presenceMeta = (p: Presence): PresenceMeta => PRESENCE_META[p] ?? PRESENCE_META.never

// Espelho do computePresence do servidor (raizRead.service.ts): cadência de
// telemetria ~5min → ao vivo até 15min, atrasado até 1h, offline depois disso.
// Existe no cliente porque nem todo endpoint manda `presence` pronto (o status
// FOTA manda só last_seen_at) — se os limiares mudarem lá, mudam aqui.
const PRESENCE_LIVE_MS = 15 * 60_000
const PRESENCE_STALE_MS = 60 * 60_000

/** Presença a partir do last_seen. `now` injetado → puro/testável. */
export function computePresence (lastSeenAt: string | null, now: Date): Presence {
  if (!lastSeenAt) return 'never'
  const diff = now.getTime() - new Date(lastSeenAt).getTime()
  if (diff <= PRESENCE_LIVE_MS) return 'live'
  if (diff <= PRESENCE_STALE_MS) return 'stale'
  return 'offline'
}

/** "há X min/h/d" curto a partir de um ISO. `now` injetado p/ teste. */
export function formatRelative (iso: string | null, now: Date = new Date()): string {
  if (!iso) return '—'
  const diffMs = now.getTime() - new Date(iso).getTime()
  if (diffMs < 60_000) return 'agora'
  const min = Math.floor(diffMs / 60_000)
  if (min < 60) return `há ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `há ${h} h`
  const d = Math.floor(h / 24)
  return `há ${d} d`
}

/**
 * Número pt-BR com casas fixas: vírgula decimal, sem separador de milhar
 * (temperaturas e tensões nunca passam de 4 dígitos). Fonte única do formato
 * numérico do produto — toda grandeza exibida passa por aqui.
 */
export const fmtNum = (v: number, digits: number): string => v.toFixed(digits).replace('.', ',')

export const fmtTemp = (v: number | null | undefined): string => (v == null ? '—' : `${fmtNum(v, 1)} °C`)
export const fmtHum = (v: number | null | undefined): string => (v == null ? '—' : `${v.toFixed(0)} %`)
export const fmtSoil = (v: number | null | undefined): string => (v == null ? '—' : `${v.toFixed(0)}`)
/** Pressão barométrica (BME280) — pt-BR, 1 casa, hPa. */
export const fmtPressure = (v: number | null | undefined): string =>
  (v == null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} hPa`)
/** Carga da bateria (%) — inteiro; só existe fora do USB. */
export const fmtBattery = (v: number | null | undefined): string => (v == null ? '—' : `${Math.round(v)}%`)

/** Tensão da bateria — pt-BR, duas casas. */
export const fmtVolts = (v: number): string => `${fmtNum(v, 2)} V`

/** "< 1 h", "~4 h", "~1,5 d", "> 7 d" — mesma régua do servidor (fmtBatteryEta em server/.../raizBattery.ts). */
export function fmtBatteryEta (hours: number): string {
  if (hours < 1) return '< 1 h'
  if (hours < 48) return `~${Math.round(hours)} h`
  if (hours < 7 * 24) return `~${(hours / 24).toFixed(1).replace('.', ',')} d`
  return '> 7 d'
}

/** Texto curto da projeção (card): "~1,5 d", "carregando", ou null quando não há o que dizer. */
export function fmtBatteryProjection (p: RaizBatteryProjection | null | undefined): string | null {
  if (p == null) return null
  if (p.state === 'charging') return 'carregando'
  // stable traz o piso inferior (≥ 300 h) → sempre "> 7 d"; discharging traz a estimativa.
  if ((p.state === 'discharging' || p.state === 'stable') && p.hours_to_floor != null) return fmtBatteryEta(p.hours_to_floor)
  return null
}

/** Caption do KPI de bateria (detalhe): projeção + tensão. undefined = sem projeção. */
export function fmtBatteryCaption (p: RaizBatteryProjection | null | undefined): string | undefined {
  if (p == null) return undefined
  const proj = fmtBatteryProjection(p)
  const tensao = fmtVolts(p.v)
  if (proj == null) return tensao
  // "acaba em" só quando é estimativa de descarga; carregando e "> 7 d" (stable) vão secos.
  return p.state === 'discharging' ? `acaba em ${proj} · ${tensao}` : `${proj} · ${tensao}`
}

// ── Saúde nomeada (1832) ─────────────────────────────────────────────────────
export type HealthColor = 'success' | 'info' | 'warning' | 'error' | 'default'

const HEALTH_COLOR: Record<RaizDeviceHealth['severity'], HealthColor> = {
  ok: 'success',
  info: 'info',
  warn: 'warning',
  critical: 'error',
  muted: 'default',
}

/**
 * Cor MUI por severidade da saúde — o vocabulário (label/causas) vem pronto do
 * servidor. Uma régua só: o chip, a linha de hibernação do card e o aviso do
 * detalhe pintam pela mesma, para nunca discordarem.
 */
export const healthColor = (s: RaizDeviceHealth['severity']): HealthColor => HEALTH_COLOR[s] ?? 'default'

/** Token de cor de TEXTO da severidade (`default` não tem `.main` no tema). */
export const healthTextColor = (s: RaizDeviceHealth['severity']): string => {
  const c = healthColor(s)
  return c === 'default' ? 'text.secondary' : `${c}.main`
}

// ── Hibernação por bateria (raiz_iot#166, FW ≥ 3.43.0) ───────────────────────
// O device se DESPEDE antes de hibernar; o servidor abre e fecha o episódio e a
// web só formata. Fuso do navegador, como o resto do painel; o formato é o do
// servidor (nota de saúde e push): "12/09, 14:32".

/** Janela em que o painel ainda sinaliza a volta ("Voltou da hibernação em …"). */
export const HIBERNATION_RETURN_WINDOW_MS = 24 * 60 * 60 * 1000

const fmtDiaHora = (d: Date): string =>
  d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
const fmtHora = (d: Date): string => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

const toDate = (iso: string | null | undefined): Date | null => {
  if (iso == null) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * "Hibernando por bateria desde 14:32 (última tensão 3,41 V)" — a frase que
 * entra no lugar do "Sem reportar". Episódio de outro dia leva a data
 * ("desde 12/09, 14:32"); sem tensão conhecida, a frase só perde o parêntese.
 */
export function fmtHibernation (h: RaizDeviceHibernation, now: Date = new Date()): string {
  const since = toDate(h.since)
  const desde = since == null ? null : since.toDateString() === now.toDateString() ? fmtHora(since) : fmtDiaHora(since)
  const tensao = h.battery_v != null ? ` (última tensão ${fmtVolts(h.battery_v)})` : ''
  return `Hibernando por bateria${desde != null ? ` desde ${desde}` : ''}${tensao}`
}

/** "Voltou da hibernação em 13/09, 09:10 (hibernava desde 12/09, 14:32)". null sem volta registrada. */
export function fmtHibernationEnded (h: RaizDeviceHibernation): string | null {
  const ended = toDate(h.ended_at)
  if (ended == null) return null
  const since = toDate(h.since)
  return `Voltou da hibernação em ${fmtDiaHora(ended)}${since != null ? ` (hibernava desde ${fmtDiaHora(since)})` : ''}`
}

/**
 * A volta ainda merece aviso? Episódio FECHADO há no máximo
 * HIBERNATION_RETURN_WINDOW_MS. Depois de um rollback do api o episódio pode
 * vir active:false sem ended_at — isso é "não está hibernando", nunca "Voltou".
 */
export function hibernationReturned (h: RaizDeviceHibernation | null | undefined, now: Date = new Date()): boolean {
  if (h == null || h.active) return false
  const ended = toDate(h.ended_at)
  // Relógio do navegador um pouco atrás do servidor dá idade negativa: conta como recente.
  return ended != null && now.getTime() - ended.getTime() <= HIBERNATION_RETURN_WINDOW_MS
}

/** Rótulo de presença do device que se despediu para hibernar (telas que só mostram presença). */
const HIBERNATING_PRESENCE_META: PresenceMeta = { label: 'Hibernando (bateria)', color: 'error' }

/**
 * Presença EFETIVA: quem se despediu para hibernar conta como `offline`,
 * qualquer que seja a presença pura. Logo depois da despedida o device ainda é
 * `live` (acabou de falar), e no carteiro isso dura um ciclo + 15 min — horas de
 * "Ao vivo" verde para uma caixa que avisou que parou. É a régua do placar do
 * servidor (summarizeDevices): filtro da frota, pino do mapa e KPI nunca
 * discordam. A `presence` crua continua sendo "quando falou por último".
 */
export function effectivePresence (d: Pick<RaizDeviceOverview, 'presence' | 'hibernation'>): Presence {
  return d.hibernation?.active === true ? 'offline' : d.presence
}

/** A última leitura NÃO é o estado atual: o sensor está offline ou hibernando. */
export function isReadingStale (d: Pick<RaizDeviceOverview, 'presence' | 'hibernation'>): boolean {
  return effectivePresence(d) === 'offline'
}

/**
 * presenceMeta das telas que mostram SÓ presença (TV, lista e balão do mapa):
 * hibernando diz "Hibernando (bateria)" na cor de offline, no lugar de "Ao vivo".
 */
export function presenceMetaFor (d: Pick<RaizDeviceOverview, 'presence' | 'hibernation'>): PresenceMeta {
  return d.hibernation?.active === true ? HIBERNATING_PRESENCE_META : presenceMeta(d.presence)
}

// ── Carteiro: o aparelho que dorme entre os envios (raiz_web#124) ────────────
// O servidor decide a cadência (raizCadence: config EM VIGOR reportada pela
// placa, fw ≥ 3.52.0, com o push ainda não confirmado por cima; fw antigo só
// pela intenção do push) e diz de onde ela veio. A web só formata — uma régua
// para o chip do card, o cabeçalho do detalhe e o tooltip, para nunca
// discordarem sobre o mesmo aparelho.

/** Primeira versão que reporta a config em vigor (espelho do CONFIG_IN_FORCE_MIN_FW do servidor). */
export const CONFIG_IN_FORCE_MIN_FW = '3.52.0'

export type CarteiroFields = Pick<
  RaizDeviceOverview, 'fw' | 'carteiro' | 'expected_interval_s' | 'cadence_source' | 'config_in_force'
>

/**
 * Duração curta pt-BR da cadência: "15 min", "1 h", "1,5 h", "25 h", "3 d".
 * Mesma régua do fmtDuration do Empurrar config (horas até 48 h): os dois
 * moram na mesma tela, e cabeçalho e card não podem dizer "1 d" onde o card de
 * config diz "24 h". Arredonda ANTES de escolher a unidade — 59,9 min é "1 h",
 * não "60 min".
 */
export function fmtCadence (s: number): string {
  const one = (x: number): string => (Number.isInteger(x) ? String(x) : x.toFixed(1).replace('.', ','))
  const min = Math.round(s / 60)
  if (min < 60) return `${Math.max(1, min)} min`
  const h = Math.round(s / 360) / 10
  if (h < 48) return `${one(h)} h`
  return `${one(Math.round(s / 8640) / 10)} d`
}

/**
 * Api antiga: o campo `carteiro` AUSENTE (undefined). Na api nova ele vem
 * sempre, true/false/null — e null é "não sei", não "api antiga".
 */
const apiAntiga = (d: CarteiroFields): boolean => d.carteiro === undefined

/**
 * Carteiro ligado? `carteiro` do servidor quando veio. Só a api antiga (sem o
 * campo) cai na cadência — ali ela só existia com o carteiro ligado pela
 * intenção. Na api nova a cadência NÃO implica carteiro: o sempre-ligado com
 * beacon acima de 5 min também tem expected_interval_s (webhookInterval × 60).
 * null (placa sem o knob, fw antigo sem push) é "não sei", nunca "ligado".
 */
const carteiroLigado = (d: CarteiroFields): boolean =>
  d.carteiro === true || (apiAntiga(d) && d.expected_interval_s != null)

/**
 * Sempre-ligado (carteiro desligado ou placa sem o knob) que fala mais devagar
 * que os ~5 min padrão: a api nova manda a cadência do beacon nele também, e a
 * presença do servidor segue essa cadência.
 */
const sempreLigadoLento = (d: CarteiroFields): boolean =>
  !apiAntiga(d) && d.carteiro !== true && d.expected_interval_s != null

/**
 * Rótulo do chip do card: "Carteiro · a cada ~1 h". null quando não está ligado
 * — desligado é o normal da frota e um chip por card só poluiria (o detalhe
 * diz "desligado" pelo carteiroHeaderText). Ligado sem cadência completa sai
 * "Carteiro" seco: o painel segue na presença padrão.
 */
export function carteiroLabel (d: CarteiroFields): string | null {
  if (!carteiroLigado(d)) return null
  return d.expected_interval_s != null ? `Carteiro · a cada ~${fmtCadence(d.expected_interval_s)}` : 'Carteiro'
}

/**
 * De onde veio o que o painel sabe do carteiro. `push` em fw < 3.52.0 NÃO é
 * "aguardando confirmar": essa placa nunca reporta a config em vigor, e dizer
 * que ela vai confirmar seria prometer o que o firmware não faz. null = api
 * antiga ou nada conhecido. Sem "ajustada no aparelho": o cfg_src é da config
 * INTEIRA (qualquer POST do hotspot), não diz que foi a cadência que mudou.
 */
export function carteiroSourceText (d: CarteiroFields): string | null {
  if (d.cadence_source === 'config_em_vigor') return 'pela config em vigor da placa'
  if (d.cadence_source === 'push') {
    return cmpFwVersions(d.fw, CONFIG_IN_FORCE_MIN_FW) === -1
      ? `pela intenção do último push (o fw ${d.fw ?? ''} não reporta a config em vigor)`
      : 'pela intenção do último push (aguardando a placa confirmar)'
  }
  return null
}

/** A placa mudou a config e o painel não recebeu a nova: o que se mostra dela pode estar velho. */
export const carteiroConfigStale = (d: Pick<RaizDeviceOverview, 'config_in_force'>): boolean =>
  d.config_in_force?.stale === true

/**
 * Explicação do "config em vigor desatualizada". Sem prometer "no próximo
 * contato": o ACK do `cfg` é qualquer 2xx, então a placa que acha que já
 * entregou só reenvia a completa no religamento ou na rodada de 24 h.
 */
export const CONFIG_STALE_HINT =
  'A placa mudou a config e o painel ainda não recebeu a nova — ligado/desligado e cadência podem estar velhos. ' +
  'Ela reenvia a config completa sozinha ao religar ou em até 24 h.'

/**
 * Tooltip do chip e do cabeçalho: o que a cadência muda na leitura do painel
 * (só vira "sem reportar" depois de um ciclo e meio — espelho de
 * presenceThresholds do servidor, que para ciclo curto ainda segura o piso de
 * 60 min), de onde ela veio e o aviso de config desatualizada. Vale para o
 * carteiro ligado e para o sempre-ligado com beacon lento (api nova). null
 * quando não há o que explicar.
 */
export function carteiroTooltip (d: CarteiroFields): string | null {
  const src = carteiroSourceText(d)
  let partes: string[]
  if (carteiroLigado(d)) {
    partes = d.expected_interval_s != null
      ? [
          `Modo carteiro: o aparelho dorme entre os envios e fala a cada ~${fmtCadence(d.expected_interval_s)}.`,
          'Só é considerado sem reportar depois de um ciclo e meio sem contato.',
          ...(src != null ? [`Cadência ${src}.`] : []),
        ]
      : [
          'Modo carteiro: o aparelho dorme entre os envios, mas a cadência não está completa — o painel segue na presença padrão (~5 min).',
          ...(src != null ? [`Ligado ${src}.`] : []),
        ]
  } else if (sempreLigadoLento(d) && d.expected_interval_s != null) {
    // carteiro null (placa sem o knob, ou desconhecido) não vira "sempre acordado": o painel não sabe.
    const quem = d.carteiro === false ? 'Carteiro desligado: o aparelho fica sempre acordado e fala' : 'O aparelho fala'
    partes = [
      `${quem} a cada ~${fmtCadence(d.expected_interval_s)}, pelo intervalo do beacon.`,
      'Só é considerado sem reportar depois de um ciclo e meio sem contato.',
      ...(src != null ? [`Cadência ${src}.`] : []),
    ]
  } else {
    return null
  }
  if (carteiroConfigStale(d)) {
    partes.push('A config em vigor está desatualizada: a placa mudou depois e o painel ainda não recebeu a nova.')
  }
  return partes.join(' ')
}

/**
 * Trecho do cabeçalho do detalhe (sem o " · "), dizendo a FONTE:
 * "carteiro ligado: fala a cada ~1 h" (config em vigor), "carteiro (pelo push):
 * fala a cada ~1 h", "carteiro desligado". Api antiga (sem o campo carteiro)
 * fica como sempre foi: "carteiro: fala a cada ~X". Sempre-ligado com beacon
 * lento (api nova) diz o ritmo: "carteiro desligado · fala a cada ~1 h", ou só
 * "fala a cada ~1 h" quando o painel não sabe do carteiro (placa sem o knob).
 * Ajuste feito no aparelho NÃO entra aqui (configLocalChangeText): o cfg_src é
 * da config inteira, não do carteiro. O aviso de desatualizada também fica fora
 * (carteiroConfigStale): vai em cor de aviso.
 */
export function carteiroHeaderText (d: CarteiroFields): string | null {
  const emVigor = d.cadence_source === 'config_em_vigor'
  if (carteiroLigado(d)) {
    const quem = emVigor ? 'carteiro ligado' : d.cadence_source === 'push' ? 'carteiro (pelo push)' : 'carteiro'
    const cadencia = d.expected_interval_s != null ? `: fala a cada ~${fmtCadence(d.expected_interval_s)}` : ''
    return `${quem}${cadencia}`
  }
  const ritmo = sempreLigadoLento(d) && d.expected_interval_s != null ? `fala a cada ~${fmtCadence(d.expected_interval_s)}` : null
  if (d.carteiro === false) {
    if (emVigor) return ritmo != null ? `carteiro desligado · ${ritmo}` : 'carteiro desligado'
    // Desligado só pela intenção (push com sleepEnabled 0 ainda sem confirmação,
    // ou fw que nunca confirma): diz, mas marca que é o pedido, não o que vale.
    if (d.cadence_source === 'push') return `carteiro desligado (pelo push)${ritmo != null ? ` · ${ritmo}` : ''}`
  }
  // Sem saber do carteiro (placa sem o knob, sleepEnabled nunca conhecido): só o
  // ritmo, quando há — "carteiro" aqui seria inventar.
  return ritmo
}

/**
 * "config alterada no aparelho em 15/09, 10:00" — a config em vigor veio do
 * hotspot (cfg_src.by = local: POST /api/sleep, /api/thresholds ou
 * /api/webhook). Vai no cabeçalho SEPARADA do carteiro: diz que a config
 * inteira mudou no aparelho, não o quê. Sem changed_at (placa sem relógio
 * naquela hora), sem data. null = não veio do aparelho, ou api antiga.
 */
export function configLocalChangeText (d: Pick<RaizDeviceOverview, 'config_in_force'>): string | null {
  if (d.config_in_force?.source !== 'local') return null
  const quando = toDate(d.config_in_force.changed_at)
  return `config alterada no aparelho${quando != null ? ` em ${fmtDiaHora(quando)}` : ''}`
}

/**
 * Posição de uma leitura em graus decimais (WGS84), no formato "lat, lon".
 * Ponto decimal — NÃO pt-BR: a vírgula já separa lat de lon, e este é o texto
 * que se cola no Google Maps/geojson.io. 5 casas ≈ 1 m (abaixo do erro do GPS).
 * Um sem o outro não localiza nada → exige o par (mesma regra do ingest).
 */
export const fmtLatLon = (lat: number | null | undefined, lon: number | null | undefined): string =>
  (lat == null || lon == null ? '—' : `${lat.toFixed(5)}, ${lon.toFixed(5)}`)

export interface CalibrationMeta {
  label: string
  color: 'success' | 'info' | 'warning' | 'default'
}

const CALIBRATION_META: Record<CalibrationStatus, CalibrationMeta> = {
  valid: { label: 'Calibração válida', color: 'success' },
  // Calibração é tarefa administrativa, não produto em risco: nunca vermelho
  // (mesma régua do CalibrationChip — docs/design-system.md).
  expiring: { label: 'Calibração a vencer', color: 'info' },
  expired: { label: 'Calibração vencida', color: 'warning' },
  none: { label: 'Sem calibração', color: 'default' },
}

export const calibrationMeta = (s: CalibrationStatus): CalibrationMeta =>
  CALIBRATION_META[s] ?? CALIBRATION_META.none

/** Dias até o vencimento (negativo = vencido). Aceita data YYYY-MM-DD ou ISO. */
export function daysUntil (iso: string | null, now: Date = new Date()): number | null {
  if (!iso) return null
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso)
  return Math.ceil((d.getTime() - now.getTime()) / 86_400_000)
}

/** Rótulos pt-BR de TODAS as categorias térmicas (superset multi-vertical — ver verticals/core.ts). */
export const CATEGORY_LABEL: Record<string, string> = {
  refrigerated: 'Refrigerado',
  frozen: 'Congelado',
  ultra_frozen: 'Ultracongelado',
  ambient: 'Ambiente',
  produce: 'Hortifrúti',
  grain: 'Grãos',
  greenhouse: 'Estufa',
  raw_milk: 'Leite cru',
  custom: 'Personalizado',
}

/** Limite de faixa: inteiro sem casa ("2"), fracionário com vírgula ("2,5") — nunca arredonda. */
const fmtBound = (v: number): string => (Number.isInteger(v) ? String(v) : fmtNum(v, 1))

// Antes era toFixed(0): uma faixa 2,5 a 7,5 °C saía "3 a 8 °C" — inclusive no PDF.
export const fmtSetpointRange = (sp: { temp_min: number, temp_max: number } | null | undefined): string =>
  sp == null ? '—' : `${fmtBound(sp.temp_min)} a ${fmtBound(sp.temp_max)} °C`

/**
 * Duração humana a partir de minutos: "5 min", "23 h 50 min", "2 d 3 h".
 * Duas unidades no máximo — "1430 min" obriga a pessoa a dividir de cabeça.
 */
export function fmtDurationMin (minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—'
  const m = Math.max(0, Math.round(minutes))
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const restMin = m % 60
  if (h < 24) return restMin > 0 ? `${h} h ${restMin} min` : `${h} h`
  const d = Math.floor(h / 24)
  const restH = h % 24
  return restH > 0 ? `${d} d ${restH} h` : `${d} d`
}

/** Data+hora pt-BR curta (ou —). */
export const fmtDateTime = (s: string | null | undefined): string =>
  (s == null ? '—' : new Date(s).toLocaleString('pt-BR'))

/**
 * Data de CALENDÁRIO (vencimento de calibração etc.) em pt-BR: "15/05/2027".
 * Recorta o AAAA-MM-DD do texto em vez de `new Date()` — meia-noite UTC vira
 * o dia anterior em Brasília.
 */
export const fmtDate = (s: string | null | undefined): string => {
  const m = s == null ? null : /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  return m == null ? (s ?? '—') : `${m[3]}/${m[2]}/${m[1]}`
}

// ── Dias de calendário do <input type="date"> → instantes para o servidor ────
// O servidor lê "2026-10-30" como meia-noite UTC = 21 h da véspera em Brasília:
// um período "até hoje" perdia o dia de hoje inteiro. Daí o dia LOCAL vira o
// intervalo [00:00, 23:59:59.999] no fuso do navegador, em ISO completo.

/** AAAA-MM-DD do dia local (não o dia UTC de `toISOString`). */
export const localYmd = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Início do dia local `ymd` em ISO UTC. */
export const dayStartIso = (ymd: string): string => new Date(`${ymd}T00:00:00`).toISOString()

/** Último milissegundo do dia local `ymd` em ISO UTC. */
export const dayEndIso = (ymd: string): string => new Date(`${ymd}T23:59:59.999`).toISOString()

// ── Excursões (fila global / unidade / sensor — rótulos únicos em pt-BR) ─────
/**
 * Excursões que ainda pedem disposição: abertas + em investigação — a mesma
 * régua da fila "Abertas". Contar só `open` fazia o sino, a TV e a Visão geral
 * esquecerem a excursão assim que alguém salvava uma nota de investigação
 * (decisão do dono em 30/10/2026). Sai do contador só com disposição final.
 */
export const pendingExcursionCount = (s: { open: number, under_investigation?: number | null } | null | undefined): number =>
  s == null ? 0 : s.open + (s.under_investigation ?? 0)

export const SEVERITY_LABEL: Record<string, string> = {
  low: 'Baixa', medium: 'Média', high: 'Alta', critical: 'Crítica',
}
export const SEVERITY_COLOR: Record<string, 'default' | 'warning' | 'error'> = {
  low: 'default', medium: 'warning', high: 'error', critical: 'error',
}

export const EXCURSION_STATUS_LABEL: Record<ExcursionStatus, string> = {
  open: 'Aberta', under_investigation: 'Em investigação', justified: 'Justificada', rejected: 'Rejeitada', closed: 'Encerrada',
}
export const EXCURSION_STATUS_COLOR: Record<ExcursionStatus, 'warning' | 'info' | 'success' | 'default'> = {
  open: 'warning', under_investigation: 'info', justified: 'success', rejected: 'default', closed: 'success',
}
